import { useEffect, useRef, useState, forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

// Photon (komoot) geocoder — free, no API key, biased to HQ in Annandale, VA.
// Returns ranked suggestions; we filter to US + format as "Street, City, ST ZIP".
const PHOTON_URL = "https://photon.komoot.io/api/";
const BIAS_LAT = 38.8304; // Annandale HQ
const BIAS_LNG = -77.1964;

function formatSuggestion(feat) {
  const p = feat.properties || {};
  const parts = [];
  if (p.housenumber && p.street) parts.push(`${p.housenumber} ${p.street}`);
  else if (p.street) parts.push(p.street);
  else if (p.name) parts.push(p.name);

  const cityLine = [p.city || p.town || p.village || p.county, p.state, p.postcode]
    .filter(Boolean)
    .join(", ")
    .replace(/, (\d{5})$/, " $1"); // ZIP without leading comma
  if (cityLine) parts.push(cityLine);

  return parts.join(", ") || p.name || "Unnamed location";
}

const AddressAutocomplete = forwardRef(function AddressAutocomplete(
  { value, onChange, onSelect, placeholder, error, testId, className = "" },
  ref
) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const skipNextFetchRef = useRef(false);

  useEffect(() => {
    // Close on outside click
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    // Skip the fetch immediately after user picked a suggestion
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const url = `${PHOTON_URL}?q=${encodeURIComponent(value)}&limit=6&lat=${BIAS_LAT}&lon=${BIAS_LNG}&lang=en`;
        const res = await fetch(url, { signal: ctrl.signal });
        const data = await res.json();
        const usOnly = (data.features || []).filter((f) => {
          const c = f?.properties?.countrycode;
          return !c || c === "US";
        });
        setSuggestions(usOnly);
        setOpen(usOnly.length > 0);
        setHighlight(-1);
      } catch (e) {
        if (e.name !== "AbortError") {
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  const pick = (feat) => {
    const formatted = formatSuggestion(feat);
    skipNextFetchRef.current = true;
    onChange(formatted);
    if (onSelect) onSelect({ formatted, feature: feat });
    setOpen(false);
    setSuggestions([]);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        ref={ref}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        className={`h-12 text-base pl-10 ${
          error
            ? "border-red-400 focus-visible:ring-red-400 bg-red-50/40"
            : "border-gray-200 focus-visible:ring-teal-500"
        } ${className}`}
      />
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500 pointer-events-none" />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500 animate-spin" />
      )}

      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          className="absolute z-50 left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-72 overflow-auto"
        >
          {suggestions.map((s, i) => {
            const label = formatSuggestion(s);
            const active = i === highlight;
            return (
              <button
                type="button"
                key={`${s.properties?.osm_id || i}-${i}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(s);
                }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-4 py-3 text-sm flex items-start gap-3 border-b border-gray-50 last:border-b-0 transition ${
                  active ? "bg-teal-50" : "hover:bg-sand"
                }`}
              >
                <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${active ? "text-teal-600" : "text-navy/40"}`} />
                <span className="text-navy leading-snug">{label}</span>
              </button>
            );
          })}
          <div className="px-4 py-2 text-[10px] text-navy/40 bg-sand/40 border-t border-gray-100">
            Address suggestions powered by OpenStreetMap
          </div>
        </div>
      )}
    </div>
  );
});

export default AddressAutocomplete;
